import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { catchError, forkJoin, map, Observable, of, Subscription, take } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { Router } from '@angular/router';

import { Facility } from '../firestore-models/facility';
import { Floor } from '../firestore-models/floor';
import { Exhibit } from '../firestore-models/exhibit';
import { ExhibitDetail } from '../firestore-models/exhibit-detail';
import { ProjectLanguage } from '../firestore-models/project-language';
import { QuestSet } from '../firestore-models/quest-set';

import { ConfigurationService } from '../configuration.service';
import { AppConst } from '../app-const';
import { Hall } from '../firestore-models/hall';

import { QrcodeService } from './services/qrcode.service';
import { SelectedLanguagesDialogComponent } from './selected-languages-dialog/selected-languages-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-qrcode',
  templateUrl: './qrcode.component.html',
  styleUrls: ['./qrcode.component.css'],
  standalone: false,
})
export class QrcodeComponent implements OnInit {

  // Facilityコレクション
  private facilitiesCollection!: AngularFirestoreCollection<Facility>;
  facilitiesObservable!: Observable<Facility[]>;
  facilitiesSubscribe!: Subscription;
  // 各展示情報に紐付くプロジェクトID
  projectId: string;
  // 各展示情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;
  selectedLanguages!: Array<ProjectLanguage>;

  // 展示情報テーブル
  displayedColumns: string[] = ['position', 'select', 'qrcodetype', 'hall_name', 'exhibit_name', 'quest_set_name', 'file'];
  dataSource = new MatTableDataSource<any>();
  selection = new SelectionModel<any>(true, []);

  searchFilter: any = {};

  uploadedFilesCollectionName = AppConst.FIRESTORE_UPLOADED_FILES;

  currentLang: string = 'ja';
  projectBaseLang: string = 'ja';
  enterNumTitle: string = '';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private router: Router,
    private qrcodeService: QrcodeService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.currentLang = this.translate.currentLang;
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.selectedLanguages = [];
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    // 施設取得
    this.facilitiesCollection = this.angularFirestore.collection<Facility>(
      AppConst.FIRESTORE_FACILITIES, ref => ref.where('project_id', '==', this.projectId).orderBy('facility_id', 'asc'));
    this.facilitiesObservable = this.facilitiesCollection.valueChanges({ idField: 'id' });
    this.facilitiesSubscribe = this.facilitiesObservable.subscribe(facilities => {
      let _data: any = [];
      if (facilities.length > 0) {
        for (const facility of facilities) {
          //#219　別名が設定された場合は「番号入力」ではなく別名を表示する
          if (facility.use_enter_num_title) {
            this.enterNumTitle = (facility.enter_num_titles.find((n: any) => n.lang_code === this.currentLang)?.name) || '';
          }
          if (facility.floor_ids && !!facility.site_url) {
            _data.push({
              qrcodetype: "ホーム",
              facility_names: facility.facility_names,
              site_url: facility.site_url,
              hall_names: [],
              floor_id: '',
              floor_names: [],
              exhibit_id: '',
              exhibit_names: [],
              exhibit_files: [],
              exhibit_detail_id: '',
              exhibit_detail_names: [],
              quest_set_id: '',
              quest_set_names: []
            });

            _data.push({
              qrcodetype: "番号入力",
              qrcodetype_disp: this.enterNumTitle ? this.enterNumTitle : this.translate.instant("番号入力"),
              facility_names: facility.facility_names,
              site_url: facility.site_url,
              hall_names: [],
              floor_id: '',
              floor_names: [],
              exhibit_id: '',
              exhibit_names: [],
              exhibit_files: [],
              exhibit_detail_id: '',
              exhibit_detail_names: [],
              quest_set_id: '',
              quest_set_names: []
            });
            _data.push({
              qrcodetype: "ランキング",
              facility_names: facility.facility_names,
              site_url: facility.site_url,
              hall_names: [],
              floor_id: '',
              floor_names: [],
              exhibit_id: '',
              exhibit_names: [],
              exhibit_files: [],
              exhibit_detail_id: '',
              exhibit_detail_names: [],
              quest_set_id: '',
              quest_set_names: []
            });

            _data.push({
              qrcodetype: "アンケート",
              facility_names: facility.facility_names,
              site_url: facility.site_url,
              hall_names: [],
              floor_id: '',
              floor_names: [],
              exhibit_id: '',
              exhibit_names: [],
              exhibit_files: [],
              exhibit_detail_id: '',
              exhibit_detail_names: [],
              quest_set_id: '',
              quest_set_names: []
            });

            this.loadQuestSet().subscribe((questSets) => {
              if (!!questSets) {
                for (const questSet of questSets) {
                  _data.push({
                    qrcodetype: "アンケート",
                    facility_names: facility.facility_names,
                    site_url: facility.site_url,
                    hall_names: [],
                    floor_id: '',
                    floor_names: [],
                    exhibit_id: '',
                    exhibit_names: [],
                    exhibit_files: [],
                    exhibit_detail_id: '',
                    exhibit_detail_names: [],
                    quest_set_id: questSet.id,
                    quest_set_names: questSet.question_titles
                  });
                }
              }

              for (const floor_id of facility.floor_ids!) {
                // フロア情報を読み込み
                this.loadFloor(floor_id).subscribe((floor) => {
                  if (floor) {
                    forkJoin(this.loadExhibits(floor), this.loadHalls(floor)).subscribe((data) => {
                      if (data[0]) {
                        let floor_output  = false
                        for (const exhibit of data[0]) {
                          if (floor_output == false) {
                            floor_output = true
                            _data.push({
                              qrcodetype: "フロア",
                              facility_names: facility.facility_names,
                              site_url: facility.site_url,
                              hall_id: data[1] && data[1].menu_disp_type == AppConst.MENU_DISP_TYPE_MENU ? data[1].hall_id : '',
                              hall_names: data[1] ? data[1].hall_names : [],
                              floor_id: floor.floor_id,
                              floor_names: floor.floor_names,
                              exhibit_id: '',
                              exhibit_names: [],
                              exhibit_files: [],
                              exhibit_detail_id: '',
                              exhibit_detail_names: [],
                              quest_set_id: '',
                              quest_set_names: []
                            });
                          }
                          _data.push({
                            qrcodetype: "展示物",
                            facility_names: facility.facility_names,
                            site_url: facility.site_url,
                            hall_names: data[1] ? data[1].hall_names : [],
                            floor_id: floor.floor_id,
                            floor_names: floor.floor_names,
                            exhibit_id: exhibit.exhibit_id,
                            exhibit_names: exhibit.exhibit_names,
                            exhibit_files: exhibit.exhibit_files,
                            exhibit_detail_id: '',
                            exhibit_detail_names: [],
                            quest_set_id: '',
                            quest_set_names: []
                          });
                          this.loadExhibitDetails(exhibit).subscribe((exhibitDetails) => {
                            console.log(exhibitDetails);
                            if (!!exhibitDetails) {
                              for (const exhibitDetail of exhibitDetails) {
                                _data.push({
                                  qrcodetype: "展示物詳細",
                                  facility_names: facility.facility_names,
                                  site_url: facility.site_url,
                                  hall_names: data[1] ? data[1].hall_names : [],
                                  floor_id: floor.floor_id,
                                  floor_names: floor.floor_names,
                                  exhibit_id: exhibit.exhibit_id,
                                  exhibit_names: exhibit.exhibit_names,
                                  exhibit_files: exhibit.exhibit_files,
                                  exhibit_detail_id: exhibitDetail.exhibit_detail_id,
                                  exhibit_detail_names: exhibitDetail.exhibit_detail_names,
                                  quest_set_id: '',
                                  quest_set_names: []
                                });
                              }
                              this.dataSource.data = this.addFilterValue(_data);
                            } else {
                              this.dataSource.data = this.addFilterValue(_data);
                            }
                          });
                        }
                      }
                    });
                  }
                });
              }

            });

            // for (const floor_id of facility.floor_ids) {
            //   // フロア情報を読み込み
            //   this.loadFloor(floor_id).subscribe((floor) => {
            //     if (floor) {
            //       forkJoin(this.loadExhibits(floor), this.loadHalls(floor)).subscribe((data) => {
            //         if (data[0]) {
            //           let floor_output  = false
            //           for (const exhibit of data[0]) {
            //             if (floor_output == false) {
            //               floor_output = true
            //               _data.push({
            //                 qrcodetype: "フロア",
            //                 facility_names: facility.facility_names,
            //                 site_url: facility.site_url,
            //                 hall_id: data[1] && data[1].menu_disp_type == AppConst.MENU_DISP_TYPE_MENU ? data[1].hall_id : '',
            //                 hall_names: data[1] ? data[1].hall_names : [],
            //                 floor_id: floor.floor_id,
            //                 floor_names: floor.floor_names,
            //                 exhibit_id: '',
            //                 exhibit_names: [],
            //                 exhibit_files: []
            //               });
            //             }
            //             _data.push({
            //               qrcodetype: "展示物",
            //               facility_names: facility.facility_names,
            //               site_url: facility.site_url,
            //               hall_names: data[1] ? data[1].hall_names : [],
            //               floor_id: floor.floor_id,
            //               floor_names: floor.floor_names,
            //               exhibit_id: exhibit.exhibit_id,
            //               exhibit_names: exhibit.exhibit_names,
            //               exhibit_files: exhibit.exhibit_files
            //             });
            //           }
            //           this.dataSource.data = this.addFilterValue(_data);
            //         }
            //       });
            //     }
            //   });
            // }
          }
        }
      }
    });
  }

  // フロア情報読み込み
  private loadFloor(floorId: string): Observable<Floor | undefined> {
    return this.angularFirestore
      .collection<Floor>(AppConst.FIRESTORE_FLOORS, (ref) => ref.where('project_id', '==', this.projectId).orderBy('floor_id', 'asc'))
      .valueChanges({ idField: 'id' })
      .pipe(
        map((floors) => {
          let floor: Floor | undefined;
          if (floors.length > 0) {
            floor = floors.find((floor) => floor.id === floorId);
          }
          return floor;
        }),
        take(1),
        catchError(() => of(undefined))
      );
  }

  // 展示情報読み込み
  private loadExhibits(floor: Floor): Observable<Exhibit[] | undefined> {
    return this.angularFirestore
      .collection<Exhibit>(AppConst.FIRESTORE_EXHIBITS, (ref) => ref.where('project_id', '==', this.projectId).orderBy('exhibit_id', 'asc'))
      .valueChanges({ idField: 'id' })
      .pipe(
        map((allExhibits) => {
          let exhibits: any = [];
          if (floor.exhibit_ids) {
            for (let exhibitId of floor.exhibit_ids) {
              const exhibit = allExhibits.filter((e) => {return e.id === exhibitId});
              if (exhibit.length >= 1) {
                exhibits.push(exhibit[0]);
              }
            }
          }
          return exhibits;
        }),
        take(1),
        catchError(() => of(undefined))
      );
  }

  // 展示詳細情報読み込み
  private loadExhibitDetails(exhibit: Exhibit): Observable<ExhibitDetail[] | undefined> {
    return this.angularFirestore
      .collection<ExhibitDetail>(AppConst.FIRESTORE_EXHIBIT_DETAILS, (ref) => ref.where('project_id', '==', this.projectId).orderBy('exhibit_detail_id', 'asc'))
      .valueChanges({ idField: 'id' })
      .pipe(
        map((allExhibitDetails) => {
          let exhibitDetails: any = [];
          if (exhibit.exhibit_detail_ids) {
            for (let exhibitDetailId of exhibit.exhibit_detail_ids) {
              const exhibitDetail = allExhibitDetails.filter((e) => {return e.id === exhibitDetailId});
              if (exhibitDetail.length >= 1) {
                exhibitDetails.push(exhibitDetail[0]);
              }
            }
          }
          return exhibitDetails;
        }),
        take(1),
        catchError(() => of(undefined))
      );
  }

  // 建物情報読み込み
  private loadHalls(floor: Floor): Observable<Hall | undefined> {
    return this.angularFirestore
      .collection<Hall>(AppConst.FIRESTORE_HALLS, (ref) => ref
      .where('project_id', '==', this.projectId)
      .where('hall_id', '==', floor.hall_id))
      .valueChanges({ idField: 'id' })
      .pipe(
        map((halls) => {
          return halls[0];
        }),
        take(1),
        catchError(() => of(undefined))
      );
  }

  // 設問セット情報読み込み
  private loadQuestSet(): Observable<QuestSet[] | undefined> {
    return this.angularFirestore
      .collection<QuestSet>(AppConst.FIRESTORE_QUEST_SET, (ref) => ref.where('project_id', '==', this.projectId).orderBy('created', 'desc'))
      .valueChanges({ idField: 'id' })
      .pipe(
        map((allQuestSets) => {
          let questSets: any = [];
          questSets = allQuestSets.filter((questSet) => {
            return !!questSet.use_question;
          });
          return questSets;
        }),
        take(1),
        catchError(() => of(undefined))
      );
  }
  

  ngOnDestroy(): void {
    this.facilitiesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Exhibit): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //言語選択ダイアログ
  openSelectLanguagesDialog(): void {
    const dialogRef = this.dialog.open(SelectedLanguagesDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { projectLanguages: this.projectLanguages, selectedLanguages: this.selectedLanguages }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        for (const row of result) {
          this.selectedLanguages.push(row);
        }
      }
    });
  }

  // QR生成
  createQRCode(): void{
    let selectedItem: any = [];
    var langParams: any = [{lang_param:'', lang_name:this.translate.instant('：言語（なし）')}];
    for (const language of this.selectedLanguages) {
      if(this.currentLang == 'ja' ) {
        langParams.push({lang_param:'lang='+language['lang_code'], lang_name:this.translate.instant('：言語（')+language['lang_name']+'）'});
      }
      else{
        langParams.push({lang_param:'lang='+language['lang_code'], lang_name:this.translate.instant('：言語（')+language['lang_name_en']+'）'});        
      }
    }
    for (const row of this.dataSource.data) {
      if (this.selection.isSelected(row)) {
        for (const langParam of langParams) {
          let siteUrl = `${row.site_url}`;
          switch(row.qrcodetype) {
            case "ホーム":
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                siteUrl: siteUrl,
                exhibitId: "",
                name: (row.facility_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.facility_names[0].name) +langParam['lang_name']
              });
              break;
            case "番号入力":
              siteUrl = siteUrl += '/keypad';
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                siteUrl: siteUrl,
                exhibitId: "",
                name: (row.facility_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.facility_names[0].name)
                  + (row.qrcodetype_disp ? row.qrcodetype_disp : this.translate.instant("番号入力"))
                  + langParam['lang_name']
              });
              break;
            case "ランキング":
              siteUrl = siteUrl += '/exhibit-rankings';
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                siteUrl: siteUrl,
                exhibitId: "",
                name:(row.facility_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.facility_names[0].name)  +this.translate.instant(":ランキング")+langParam
              });
              break;
            case "アンケート":
              if (row.quest_set_id) {
                siteUrl = siteUrl += '/enquete';
                siteUrl = siteUrl += '/' + row.quest_set_id;
              } else {
                siteUrl = siteUrl += '/enquete-title';
              }
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                siteUrl: siteUrl,
                exhibitId: row.quest_set_id,
                name: (
                  row.quest_set_names.length > 0
                    ? (row.quest_set_names.find((n: any) => n.lang_code === this.projectBaseLang)?.string_message ?? row.quest_set_names[0].string_message)
                    : ((row.facility_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.facility_names[0].name) + this.translate.instant(":アンケート"))
                ) + langParam['lang_name']
              });
              break;
            case "フロア":
              siteUrl = siteUrl += '/floors/' + row.floor_id;
              if (row.hall_id) {
                siteUrl += '?hall_id=' + row.hall_id;
              }
              if (langParam['lang_param']!='') {
                if (row.hall_id) {
                  siteUrl += '&'+langParam['lang_param'];
                } else {
                  siteUrl += '?'+langParam['lang_param'];
                }
              }
              selectedItem.push({
                siteUrl: siteUrl,
                exhibitId: row.floor_id,
                name: (row.floor_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.floor_names[0].name) + langParam['lang_name']
              });
              break;
            case "展示物":
              siteUrl = siteUrl += '/exhibits/' + row.exhibit_id;
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                siteUrl: siteUrl,
                exhibitId: row.exhibit_id,
                name: (row.exhibit_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.floor_names[0].name) +langParam['lang_name']
              });
              break;
            case "展示物詳細":
              siteUrl = siteUrl += '/exhibit-details/' + row.exhibit_detail_id;
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                siteUrl: siteUrl,
                exhibitId: row.exhibit_detail_id,
                name: (row.exhibit_detail_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.floor_names[0].name) +langParam['lang_name']
              });
              break;
            default:
              break;
          }
        }
      }
    }
    this.qrcodeService.qrdata = selectedItem;
    this.router.navigate([AppConst.ROUTING_LINK_QRCODE_RESULT]);
    // const url = `${location.protocol}//${location.host}${AppConst.ROUTING_LINK_QRCODE_RESULT}`;
    // console.log(url);
    // window.open(url, '_blank');
  }

  // URL生成
  createURLCode(): void{
    let selectedItem: any = [];
    var langParams: any = [{lang_param:'', lang_name:'ー'}];
    for (const language of this.selectedLanguages) {
      langParams.push({lang_param:'lang='+language['lang_code'], lang_name:language['lang_name'],lang_name_en:language['lang_name_en']});
    }
    for (const row of this.dataSource.data) {
      if (this.selection.isSelected(row)) {
        for (const langParam of langParams) {
          let siteUrl = `${row.site_url}`;
          switch(row.qrcodetype) {
            case "ホーム":
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                qrcodeType: this.translate.instant(row.qrcodetype),
                siteUrl: siteUrl,
                exhibitId: "",
                name: (row.facility_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.facility_names[0].name),
                lang: this.projectBaseLang === 'ja' ? langParam['lang_name'] : langParam['lang_name_en']
              });
              break;
            case "番号入力":
              siteUrl = siteUrl += '/keypad';
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                qrcodeType: row.qrcodetype_disp ? row.qrcodetype_disp : this.translate.instant(row.qrcodetype),
                siteUrl: siteUrl,
                exhibitId: "",
                name: (row.facility_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.facility_names[0].name),
                lang: this.projectBaseLang === 'ja' ? langParam['lang_name'] : langParam['lang_name_en']
              });
              break;
            case "ランキング":
              siteUrl = siteUrl += '/exhibit-rankings';
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                qrcodeType: this.translate.instant(row.qrcodetype),
                siteUrl: siteUrl,
                exhibitId: "",
                name: (row.facility_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.facility_names[0].name),
                lang: this.projectBaseLang === 'ja' ? langParam['lang_name'] : langParam['lang_name_en']
              });
              break;
            case "アンケート":
              if (row.quest_set_id) {
                siteUrl = siteUrl += '/enquete';
                siteUrl = siteUrl += '/' + row.quest_set_id;
              } else {
                siteUrl = siteUrl += '/enquete-title';
              }
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                qrcodeType: this.translate.instant(row.qrcodetype),
                siteUrl: siteUrl,
                exhibitId: row.quest_set_id,
                name: row.quest_set_names.length > 0 ? (row.quest_set_names.find((n: any) => n.lang_code === this.projectBaseLang)?.string_message ?? row.quest_set_names[0].string_message) : (row.facility_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.facility_names[0].name),
                lang: this.projectBaseLang === 'ja' ? langParam['lang_name'] : langParam['lang_name_en']
              });
              break;
            case "フロア":
              siteUrl = siteUrl += '/floors/' + row.floor_id;
              if (row.hall_id) {
                siteUrl += '?hall_id=' + row.hall_id;
              }
              if (langParam['lang_param']!='') {
                if (row.hall_id) {
                  siteUrl += '&'+langParam['lang_param'];
                } else {
                  siteUrl += '?'+langParam['lang_param'];
                }
              }
              selectedItem.push({
                qrcodeType: this.translate.instant(row.qrcodetype),
                siteUrl: siteUrl,
                exhibitId: row.floor_id,
                name: (row.floor_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.floor_names[0].name),
                lang: this.projectBaseLang === 'ja' ? langParam['lang_name'] : langParam['lang_name_en']
              });
              break;
            case "展示物":
              siteUrl = siteUrl += '/exhibits/' + row.exhibit_id;
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                qrcodeType: this.translate.instant(row.qrcodetype),
                siteUrl: siteUrl,
                exhibitId: row.exhibit_id,
                name: (row.exhibit_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.floor_names[0].name),
                lang: this.projectBaseLang === 'ja' ? langParam['lang_name'] : langParam['lang_name_en']
              });
              break;
            case "展示物詳細":
              siteUrl = siteUrl += '/exhibit-details/' + row.exhibit_detail_id;
              if (langParam['lang_param']!='') {
                siteUrl += '?'+langParam['lang_param'];
              }
              selectedItem.push({
                qrcodeType: this.translate.instant(row.qrcodetype),
                siteUrl: siteUrl,
                exhibitId: row.exhibit_detail_id,
                name: (row.exhibit_detail_names.find((n: any) => n.lang_code === this.projectBaseLang)?.name ?? row.floor_names[0].name),
                lang: this.projectBaseLang === 'ja' ? langParam['lang_name'] : langParam['lang_name_en']
              });
              break;
            default:
              break;
          }
        }
      }
    }
    this.qrcodeService.qrdata = selectedItem;
    this.router.navigate([AppConst.ROUTING_LINK_URL_RESULT]);
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      let aName = '';
      let bName = '';
      let aNames = [];
      let bNames = [];
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'exhibit_id':
          return compare(a.exhibit_id, b.exhibit_id, isAsc);
        case 'facility_name':
          aName = a.facility_names.filter((d: any) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          bName = b.facility_names.filter((d: any) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        case 'quest_set_name':
          aNames = a.quest_set_names.filter((d: any) => { return d.lang_code == this.projectLanguages[0].lang_code});
          aName = aNames.length > 0 ? aNames[0].string_message : '';
          bNames = b.quest_set_names.filter((d: any) => { return d.lang_code == this.projectLanguages[0].lang_code});
          bName = bNames.length > 0 ? bNames[0].string_message : '';
          return compare(aName, bName, isAsc);
        case 'hall_name':
          aNames = a.hall_names.filter((d: any) => { return d.lang_code == this.projectLanguages[0].lang_code});
          aName = aNames.length > 0 ? aNames[0].name : '';
          bNames = b.hall_names.filter((d: any) => { return d.lang_code == this.projectLanguages[0].lang_code});
          bName = bNames.length > 0 ? bNames[0].name : '';
          return compare(aName, bName, isAsc);
        case 'floor_name':
          aNames = a.floor_names.filter((d: any) => { return d.lang_code == this.projectLanguages[0].lang_code});
          aName = aNames.length > 0 ? aNames[0].name : '';
          bNames = b.floor_names.filter((d: any) => { return d.lang_code == this.projectLanguages[0].lang_code});
          bName = bNames.length > 0 ? bNames[0].name : '';
          return compare(aName, bName, isAsc);
        case 'exhibit_name':
          aNames = a.exhibit_names.filter((d: any) => { return d.lang_code == this.projectLanguages[0].lang_code});
          aName = aNames.length > 0 ? aNames[0].name : '';
          bNames = b.exhibit_names.filter((d: any) => { return d.lang_code == this.projectLanguages[0].lang_code});
          bName = bNames.length > 0 ? bNames[0].name : '';
          return compare(aName, bName, isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      filterValue += doc.exhibit_id;
      for (const value of doc.facility_names) {
        filterValue += value.name + ' '
      }
      for (const value of doc.quest_set_names) {
        filterValue += value.string_message + ' '
      }
      for (const value of doc.hall_names) {
        filterValue += value.name + ' '
      }
      for (const value of doc.floor_names) {
        filterValue += value.name + ' '
      }
      for (const value of doc.exhibit_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterAttr(event: any) {
    this.selection.clear();
    let filterValue = '';
    this.searchFilter['filterValueAttr'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

}
